/**
 * NavigableTree - ARIA tree structure for molecular SVG accessibility
 *
 * Renders an invisible ARIA tree structure within the SVG that allows
 * screen reader users to navigate the molecular structure hierarchically.
 *
 * Features:
 * - Hierarchical navigation (molecules > features > atoms/bonds > lone pairs)
 * - Keyboard navigation (arrow keys, Enter, Escape)
 * - Selection callbacks for integration with visual selection
 * - Independent from visual SVG elements (uses data-ref-* attributes)
 *
 * @package @alchemiesolns/svg-interactive
 */

import React, { useCallback, useRef, useState, useEffect, useMemo, useId, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import type {
  SVGStructure,
  MoleculeNode,
  FeatureNode,
  AtomLeaf,
  LonePairLeaf,
  BondLeaf,
  BondEndpointLeaf,
} from '../structure/types';
import type { TreeLabelMap } from '../structure/buildAltTextMap';
import { buildTreeNavigation, treeNavHelpers } from '../structure/buildStructure';

export type ElementType = 'atom' | 'bond' | 'bond-endpoint' | 'lonepair' | 'feature' | 'molecule';

/**
 * Imperative handle for NavigableTree
 * Allows parent components to control tree state programmatically
 */
export interface NavigableTreeHandle {
  /** Reset tree to initial state: collapse all nodes, clear focus */
  reset: () => void;
  /** Expand all nodes in the tree (useful for SVG download with full structure) */
  expandAll: () => void;
}

export interface NavigableTreeProps {
  /** The molecular structure to render */
  structure: SVGStructure;
  /** Map of element IDs to tree labels (name + optional description). */
  treeLabelMap: TreeLabelMap;
  /** Callback when an element is selected (activated via Enter/Space) */
  onSelect?: (elementId: string, elementType: ElementType) => void;
  /** Callback when focus changes to a different element */
  onFocusChange?: (elementId: string | null, elementType: ElementType | null) => void;
  /** Callback when expanded IDs change (for syncing with debug panel) */
  onExpandChange?: (expandedIds: Set<string>) => void;
  /** Whether the tree should be visible (for debugging) */
  debugVisible?: boolean;
  /** External selection set — when provided, aria-selected reflects this set instead of focus */
  selectedIds?: Set<string>;
  /**
   * Optional predicate to check if an element can be selected.
   * Called BEFORE selecting and announcing. If returns false, selection is blocked.
   * This allows external logic (like selection overseers) to control what can be
   * selected without NavigableTree knowing the business rules.
   */
  canSelect?: (elementId: string, elementType: ElementType) => boolean;
  /**
   * Start with all nodes expanded (useful for SVG export/download).
   * When true, all expandable nodes will be expanded on initial render.
   */
  defaultExpandAll?: boolean;
}

/**
 * Get the element type from a node ID
 */
function getElementType(id: string, structure: SVGStructure): ElementType {
  if (structure.index.moleculesById.has(id)) return 'molecule';
  if (structure.index.featuresById.has(id)) return 'feature';
  if (structure.index.atomsById.has(id)) return 'atom';
  if (structure.index.bondsById.has(id)) return 'bond';
  if (structure.index.endpointsById.has(id)) return 'bond-endpoint';
  if (structure.index.lonePairsById.has(id)) return 'lonepair';
  return 'atom'; // default
}

/**
 * Render a lone pair tree item (leaf node - no children)
 *
 * Note: lonePair.id is the tree navigation ID (may be feature-scoped)
 *       lonePair.atomId + '-lp' + lonePair.index gives the original SVG element ID
 */
function LonePairTreeItem({
  lonePair,
  altText,
  isFocused,
  treeItemDomId,
  selectedIds,
}: {
  lonePair: LonePairLeaf;
  altText: string;
  isFocused: boolean;
  treeItemDomId: (chemistryId: string) => string;
  selectedIds?: Set<string>;
}) {
  // Construct original lone pair ID for SVG reference
  const originalLpId = `${lonePair.atomId}-lp${lonePair.index}`;

  // Lone pairs are leaf nodes - no aria-expanded needed
  return (
    <g
      id={treeItemDomId(lonePair.id)}  // Use tree ID for DOM element
      role="treeitem"
      aria-label={altText}
      aria-selected={selectedIds ? selectedIds.has(originalLpId) : isFocused}
      tabIndex={-1}
    />
  );
}

/**
 * Render a bond endpoint tree item (leaf node - no children)
 */
function BondEndpointTreeItem({
  endpoint,
  isFocused,
  treeItemDomId,
  selectedIds,
}: {
  endpoint: BondEndpointLeaf;
  isFocused: boolean;
  treeItemDomId: (id: string) => string;
  selectedIds?: Set<string>;
}) {
  // Original endpoint ID format: bondId:atomId
  const originalId = `${endpoint.bondId}:${endpoint.atomId}`;

  return (
    <g
      id={treeItemDomId(endpoint.id)}
      role="treeitem"
      aria-label={endpoint.label}
      aria-selected={selectedIds ? selectedIds.has(originalId) : isFocused}
      tabIndex={-1}
    />
  );
}

/**
 * Render a bond tree item with nested endpoints
 */
function BondTreeItem({
  bond,
  focusedId,
  isExpanded,
  treeItemDomId,
  selectedIds,
}: {
  bond: BondLeaf;
  focusedId: string | null;
  isExpanded: boolean;
  treeItemDomId: (id: string) => string;
  selectedIds?: Set<string>;
}) {
  const hasEndpoints = bond.endpointNodes.length > 0;
  const isFocused = focusedId === bond.id;

  return (
    <g
      id={treeItemDomId(bond.id)}
      role="treeitem"
      aria-label={bond.label}
      aria-selected={selectedIds ? selectedIds.has(bond.bondId) : isFocused}
      aria-expanded={hasEndpoints ? isExpanded : undefined}
      tabIndex={-1}
    >
      {hasEndpoints && isExpanded && (
        <g role="group" aria-label="Endpoints">
          {bond.endpointNodes.map((ep) => (
            <BondEndpointTreeItem
              key={ep.id}
              endpoint={ep}
              isFocused={focusedId === ep.id}
              treeItemDomId={treeItemDomId}
              selectedIds={selectedIds}
            />
          ))}
        </g>
      )}
    </g>
  );
}

/**
 * Render an atom tree item with nested hydrogens, lone pairs, and bonds
 *
 * Note: atom.id is the tree navigation ID (may be feature-scoped like "feature-1:atom-1")
 *       atom.atomId is the original atom ID for SVG element reference
 */
function AtomTreeItem({
  atom,
  treeLabelMap,
  focusedId,
  isExpanded,
  expandedIds,
  treeItemDomId,
  selectedIds,
}: {
  atom: AtomLeaf;
  treeLabelMap: TreeLabelMap;
  focusedId: string | null;
  isExpanded: boolean;
  expandedIds: Set<string>;
  treeItemDomId: (chemistryId: string) => string;
  selectedIds?: Set<string>;
}) {
  const hasHydrogens = atom.hydrogenNodes.length > 0;
  const hasLonePairs = atom.lonePairNodes.length > 0;
  const hasBonds = atom.bondNodes.length > 0;
  const hasChildren = hasHydrogens || hasLonePairs || hasBonds;
  const atomAltText = treeLabelMap.get(atom.atomId)?.name || atom.label;
  const isFocused = focusedId === atom.id;  // Use tree ID for focus comparison

  return (
    <g
      id={treeItemDomId(atom.id)}  // Use tree ID for DOM element
      role="treeitem"
      aria-label={atomAltText}
      aria-selected={selectedIds ? selectedIds.has(atom.atomId) : isFocused}
      aria-expanded={hasChildren ? isExpanded : undefined}
      tabIndex={-1}
    >
      {hasChildren && isExpanded && (
        <g role="group" aria-label="Children">
          {/* Render hydrogen children first */}
          {atom.hydrogenNodes.map((h) => (
            <AtomTreeItem
              key={h.id}
              atom={h}
              treeLabelMap={treeLabelMap}
              focusedId={focusedId}
              isExpanded={expandedIds.has(h.id)}  // Use tree ID for expansion
              expandedIds={expandedIds}
              treeItemDomId={treeItemDomId}
              selectedIds={selectedIds}
            />
          ))}
          {/* Then render lone pairs */}
          {atom.lonePairNodes.map((lp) => (
            <LonePairTreeItem
              key={lp.id}
              lonePair={lp}
              altText={treeLabelMap.get(lp.atomId + '-lp' + lp.index)?.name || lp.label}
              isFocused={focusedId === lp.id}  // Use tree ID
              treeItemDomId={treeItemDomId}
              selectedIds={selectedIds}
            />
          ))}
          {/* Then render bonds */}
          {atom.bondNodes.map((bond) => (
            <BondTreeItem
              key={bond.id}
              bond={bond}
              focusedId={focusedId}
              isExpanded={expandedIds.has(bond.id)}
              treeItemDomId={treeItemDomId}
              selectedIds={selectedIds}
            />
          ))}
        </g>
      )}
    </g>
  );
}

/**
 * Render a feature tree item with nested atoms and child features
 */
function FeatureTreeItem({
  feature,
  treeLabelMap,
  focusedId,
  expandedIds,
  treeItemDomId,
  selectedIds,
}: {
  feature: FeatureNode;
  treeLabelMap: TreeLabelMap;
  focusedId: string | null;
  expandedIds: Set<string>;
  treeItemDomId: (chemistryId: string) => string;
  selectedIds?: Set<string>;
}) {
  const featureAltText = treeLabelMap.get(feature.id)?.name || feature.label;
  const isFocused = focusedId === feature.id;
  const hasAtoms = feature.atoms.length > 0;
  const hasChildFeatures = feature.children.length > 0;
  const hasChildren = hasAtoms || hasChildFeatures;
  const isExpanded = expandedIds.has(feature.id);

  // Helper to render children in interleaved order (matching NC TreeSelect)
  const renderChildren = () => {
    // Use orderedChildIds for proper interleaving of atoms and child features
    if (feature.orderedChildIds && feature.orderedChildIds.length > 0) {
      return feature.orderedChildIds.map((childId) => {
        // Find atom with this ID
        const atom = feature.atoms.find(a => a.id === childId);
        if (atom) {
          return (
            <AtomTreeItem
              key={atom.id}
              atom={atom}
              treeLabelMap={treeLabelMap}
              focusedId={focusedId}
              isExpanded={expandedIds.has(atom.id)}
              expandedIds={expandedIds}
              treeItemDomId={treeItemDomId}
              selectedIds={selectedIds}
            />
          );
        }
        // Find child feature with this ID
        const childFeature = feature.children.find(f => f.id === childId);
        if (childFeature) {
          return (
            <FeatureTreeItem
              key={childFeature.id}
              feature={childFeature}
              treeLabelMap={treeLabelMap}
              focusedId={focusedId}
              expandedIds={expandedIds}
              treeItemDomId={treeItemDomId}
              selectedIds={selectedIds}
            />
          );
        }
        return null;
      });
    }

    // Fallback: atoms first, then child features
    return (
      <>
        {feature.atoms.map((atom) => (
          <AtomTreeItem
            key={atom.id}
            atom={atom}
            treeLabelMap={treeLabelMap}
            focusedId={focusedId}
            isExpanded={expandedIds.has(atom.id)}
            expandedIds={expandedIds}
            treeItemDomId={treeItemDomId}
            selectedIds={selectedIds}
          />
        ))}
        {feature.children.map((childFeature) => (
          <FeatureTreeItem
            key={childFeature.id}
            feature={childFeature}
            treeLabelMap={treeLabelMap}
            focusedId={focusedId}
            expandedIds={expandedIds}
            treeItemDomId={treeItemDomId}
            selectedIds={selectedIds}
          />
        ))}
      </>
    );
  };

  return (
    <g
      id={treeItemDomId(feature.id)}
      role="treeitem"
      aria-label={featureAltText}
      aria-selected={selectedIds ? selectedIds.has(feature.id) : isFocused}
      aria-expanded={hasChildren ? isExpanded : undefined}
      tabIndex={-1}
    >
      {hasChildren && isExpanded && (
        <g role="group" aria-label="Elements">
          {renderChildren()}
        </g>
      )}
    </g>
  );
}

/**
 * Render a molecule tree item with nested features and unclaimed elements
 */
function MoleculeTreeItem({
  molecule,
  treeLabelMap,
  focusedId,
  expandedIds,
  treeItemDomId,
  selectedIds,
}: {
  molecule: MoleculeNode;
  treeLabelMap: TreeLabelMap;
  focusedId: string | null;
  expandedIds: Set<string>;
  treeItemDomId: (chemistryId: string) => string;
  selectedIds?: Set<string>;
}) {
  const moleculeAltText = treeLabelMap.get(molecule.id)?.name || molecule.label;
  const isFocused = focusedId === molecule.id;
  const hasFeatures = molecule.features.length > 0;
  const hasUnclaimed = molecule.unclaimedAtoms.length > 0;
  const hasChildren = hasFeatures || hasUnclaimed;
  const isExpanded = expandedIds.has(molecule.id);

  return (
    <g
      id={treeItemDomId(molecule.id)}
      role="treeitem"
      aria-label={moleculeAltText}
      aria-selected={selectedIds ? selectedIds.has(molecule.id) : isFocused}
      aria-expanded={hasChildren ? isExpanded : undefined}
      tabIndex={-1}
    >
      {hasChildren && isExpanded && (
        <g role="group" aria-label="Contents">
          {/* Render features */}
          {molecule.features.map((feature) => (
            <FeatureTreeItem
              key={feature.id}
              feature={feature}
              treeLabelMap={treeLabelMap}
              focusedId={focusedId}
              expandedIds={expandedIds}
              treeItemDomId={treeItemDomId}
              selectedIds={selectedIds}
            />
          ))}

          {/* Render unclaimed atoms */}
          {molecule.unclaimedAtoms.map((atom) => (
            <AtomTreeItem
              key={atom.id}
              atom={atom}
              treeLabelMap={treeLabelMap}
              focusedId={focusedId}
              isExpanded={expandedIds.has(atom.id)}
              expandedIds={expandedIds}
              treeItemDomId={treeItemDomId}
              selectedIds={selectedIds}
            />
          ))}
        </g>
      )}
    </g>
  );
}

/**
 * NavigableTree component
 *
 * Renders an ARIA tree structure within the SVG for screen reader navigation.
 * The tree is invisible but navigable via keyboard.
 *
 * Navigation:
 * - ArrowDown: Move to next visible item (child if expanded, or next sibling)
 * - ArrowUp: Move to previous visible item
 * - ArrowRight: Expand node or move to first child
 * - ArrowLeft: Collapse node or move to parent
 * - Enter/Space: Activate (select) the current node
 * - Home: Move to first item
 * - End: Move to last visible item
 */
export const NavigableTree = forwardRef<NavigableTreeHandle, NavigableTreeProps>(
  function NavigableTree(
    {
      structure,
      treeLabelMap,
      onSelect,
      onFocusChange,
      onExpandChange,
      debugVisible = false,
      selectedIds,
      canSelect,
      defaultExpandAll = false,
    },
    ref
  ) {
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [liveText, setLiveText] = useState('');
  const treeRef = useRef<SVGGElement>(null);

  // Generate unique DOM IDs for treeitem elements
  const instanceId = useId();
  const treeItemDomId = useCallback(
    (chemistryId: string) => `${instanceId}-tree-${chemistryId}`,
    [instanceId],
  );

  // Build tree navigation structure
  const nav = useMemo(() => buildTreeNavigation(structure), [structure]);

  // Expose imperative handle for parent to reset tree state
  useImperativeHandle(ref, () => ({
    reset: () => {
      setExpandedIds(new Set());
      setFocusedId(null);
      // Notify parent of state changes
      onExpandChange?.(new Set());
      onFocusChange?.(null, null);
    },
    expandAll: () => {
      // Collect all node IDs that have children (from nav.childrenOf)
      const allExpandableIds = new Set<string>();
      for (const [nodeId, children] of nav.childrenOf.entries()) {
        if (children.length > 0) {
          allExpandableIds.add(nodeId);
        }
      }
      setExpandedIds(allExpandableIds);
      onExpandChange?.(allExpandableIds);
    },
  }), [onExpandChange, onFocusChange, nav]);

  // Initialize expanded state - start collapsed so screen readers don't read all children at once
  // Users expand nodes with ArrowRight key to drill down into the hierarchy
  // Unless defaultExpandAll is true, then start fully expanded (for export)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    if (defaultExpandAll) {
      // Compute initial nav for expanded state
      const initialNav = buildTreeNavigation(structure);
      const allExpandableIds = new Set<string>();
      for (const [nodeId, children] of initialNav.childrenOf.entries()) {
        if (children.length > 0) {
          allExpandableIds.add(nodeId);
        }
      }
      return allExpandableIds;
    }
    return new Set<string>();
  });

  // Reset expanded state when structure changes (new molecule loaded)
  // If defaultExpandAll, re-expand all nodes
  useEffect(() => {
    if (defaultExpandAll) {
      const allExpandableIds = new Set<string>();
      for (const [nodeId, children] of nav.childrenOf.entries()) {
        if (children.length > 0) {
          allExpandableIds.add(nodeId);
        }
      }
      setExpandedIds(allExpandableIds);
    } else {
      setExpandedIds(new Set<string>());
    }
  }, [structure, defaultExpandAll, nav]);

  // Notify parent when expanded state changes
  useEffect(() => {
    onExpandChange?.(expandedIds);
  }, [expandedIds, onExpandChange]);

  // Update live region text when focused item changes
  useEffect(() => {
    if (focusedId) {
      // Tree IDs are in format "featureId:originalId" (e.g., "fe-16:aEpW8Xy")
      // treeLabelMap is keyed by original IDs, so extract the part after the colon
      const originalId = focusedId.includes(':') ? focusedId.split(':')[1] : focusedId;
      const entry = treeLabelMap.get(originalId) || treeLabelMap.get(focusedId);
      const label = entry?.name || 'Unknown element';
      setLiveText(label);
    } else {
      setLiveText('');
    }
  }, [focusedId, treeLabelMap]);

  // Toggle expansion of a node
  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Focus a node and notify listeners
  const focusNode = useCallback((id: string | null) => {
    setFocusedId(id);
    if (id !== null) {
      const elementType = getElementType(id, structure);
      onFocusChange?.(id, elementType);
    } else {
      onFocusChange?.(null, null);
    }
  }, [structure, onFocusChange]);

  // Handle element activation (Enter/Space)
  const handleActivate = useCallback((id: string) => {
    const elementType = getElementType(id, structure);
    onSelect?.(id, elementType);
  }, [structure, onSelect]);

  // Handle keyboard navigation at the tree level
  const handleTreeKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (!focusedId) {
      // No focus yet, focus first molecule
      if (nav.rootIds.length > 0) {
        focusNode(nav.rootIds[0]);
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        const nextId = treeNavHelpers.getNextVisible(nav, focusedId, expandedIds);
        if (nextId) {
          focusNode(nextId);
        }
        break;
      }

      case 'ArrowUp': {
        event.preventDefault();
        const prevId = treeNavHelpers.getPrevVisible(nav, focusedId, expandedIds);
        if (prevId) {
          focusNode(prevId);
        }
        break;
      }

      case 'ArrowRight': {
        event.preventDefault();
        if (treeNavHelpers.hasChildren(nav, focusedId)) {
          if (!expandedIds.has(focusedId)) {
            // Expand the node
            toggleExpanded(focusedId);
          } else {
            // Already expanded, move to first child
            const firstChild = treeNavHelpers.getFirstChild(nav, focusedId);
            if (firstChild) {
              focusNode(firstChild);
            }
          }
        }
        break;
      }

      case 'ArrowLeft': {
        event.preventDefault();
        if (expandedIds.has(focusedId) && treeNavHelpers.hasChildren(nav, focusedId)) {
          // Collapse the node
          toggleExpanded(focusedId);
        } else {
          // Move to parent
          const parent = treeNavHelpers.getParent(nav, focusedId);
          if (parent) {
            focusNode(parent);
          }
        }
        break;
      }

      case 'Home': {
        event.preventDefault();
        if (nav.rootIds.length > 0) {
          focusNode(nav.rootIds[0]);
        }
        break;
      }

      case 'End': {
        event.preventDefault();
        // Find last visible node
        if (nav.rootIds.length > 0) {
          const lastRoot = nav.rootIds[nav.rootIds.length - 1];
          const lastVisible = treeNavHelpers.getLastVisibleDescendant(nav, lastRoot, expandedIds);
          focusNode(lastVisible);
        }
        break;
      }

      case 'Enter':
      case ' ': {
        event.preventDefault();
        // Check if selection is allowed before selecting
        const elementType = getElementType(focusedId, structure);
        if (canSelect && !canSelect(focusedId, elementType)) {
          // Selection not allowed - announce and return without selecting
          setLiveText(`Cannot select ${elementType}. Select a lone pair or bond endpoint first.`);
          return;
        }
        handleActivate(focusedId);
        break;
      }

      case 'Escape': {
        event.preventDefault();
        focusNode(null);
        break;
      }
    }
  }, [focusedId, nav, expandedIds, focusNode, toggleExpanded, handleActivate, canSelect, structure]);

  // Set initial focus to first molecule when tree gains focus
  const handleTreeFocus = useCallback(() => {
    if (focusedId === null && nav.rootIds.length > 0) {
      focusNode(nav.rootIds[0]);
    }
  }, [focusedId, nav.rootIds, focusNode]);

  const treeLabel = structure.tree.label;
  const treeDescription = structure.tree.description;

  return (
    <>
      <g
        ref={treeRef}
        role="tree"
        aria-label={treeLabel}
        aria-description={treeDescription}
        aria-activedescendant={focusedId ? treeItemDomId(focusedId) : undefined}
        tabIndex={0}
        onKeyDown={handleTreeKeyDown}
        onFocus={handleTreeFocus}
        style={{
          // Make tree invisible but still accessible
          // pointerEvents: none so mouse clicks pass through to the main SVG
          // Screen readers and keyboard navigation still work
          opacity: debugVisible ? 0.1 : 0,
          pointerEvents: 'none',
        }}
      >
        {structure.tree.molecules.map((molecule) => (
          <MoleculeTreeItem
            key={molecule.id}
            molecule={molecule}
            treeLabelMap={treeLabelMap}
            focusedId={focusedId}
            expandedIds={expandedIds}
            treeItemDomId={treeItemDomId}
            selectedIds={selectedIds}
          />
        ))}
      </g>
      {typeof document !== 'undefined' && createPortal(
        <div
          aria-live="assertive"
          aria-atomic="true"
          role="status"
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {liveText}
        </div>,
        document.body,
      )}
    </>
  );
});

export default NavigableTree;